import { faTimes } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useEffect, useMemo, useState } from 'react';
import { CourseEnrolment } from 'src/ReduxStorage/InstitutionAdminSettings/types';
import { MultiSelect } from 'src/web/components/Input/DropdownsOld/MultiSelect/component';
import { ModalDialog } from 'src/web/components/Modals/ModalDialog';
import { useGetInstitutionCohortsByCoursePathApi } from '../../../hooks';
import {
  StyledTimesButton,
  CourseDetailItem,
  CourseName,
  CoursePath,
  TotalSelectedMembers,
  ErrorMessage,
} from '../../../styles';
import { AssignedCourseRolesType } from '../../../types';
import {
  CoursesListTable,
  ClassSelectWrapper,
  CourseItemTr,
  CourseItemTd,
  CourseItemTh,
} from './styles';

type ClassStateType = string[];

const ClassOptions = ({
  institutionPath,
  coursePath,
  onChange,
}: {
  institutionPath: string;
  coursePath: string;
  onChange: (param: ClassStateType) => void;
}): JSX.Element => {
  const [selectedClass, setSelectedClass] = useState<
    Readonly<{ label: string; value: string }[]>
  >([]);
  const [classState, setClassState] = useState<ClassStateType>([]);

  const cohortsData = useGetInstitutionCohortsByCoursePathApi(
    institutionPath,
    coursePath
  );

  useEffect(() => {
    setClassState(selectedClass.map(({ value }) => value));
  }, [selectedClass]);

  useEffect(() => {
    onChange(classState);
  }, [classState]);

  const classOptions = useMemo(
    () =>
      cohortsData
        ? cohortsData.courses[0]?.classes.map((cohort) => ({
            label: `${cohort.name} ${
              cohort.numEnrolled ? `(${cohort.numEnrolled} learners)` : ''
            }`,
            value: cohort.path,
          }))
        : [],
    [cohortsData]
  );

  return (
    <div>
      <MultiSelect
        placeholder="Add a class"
        options={classOptions}
        value={selectedClass}
        onChange={(values) => setSelectedClass(values)}
      />
    </div>
  );
};

export interface EnrolCoursesModalProps {
  isAssigningFromCoursesLoading: boolean;
  totalSelectedMembers: number;
  errorMessage: string;
  courses: CourseEnrolment[];
  institutionPath: string;
  onAssignToCourses: (param: AssignedCourseRolesType) => void;
  onHide: () => void;
}

export const EnrolCoursesModal: React.FC<EnrolCoursesModalProps> = ({
  isAssigningFromCoursesLoading,
  totalSelectedMembers,
  errorMessage,
  institutionPath,
  courses,
  onHide,
  onAssignToCourses,
}) => {
  const [selectedCourses, setSelectedCourses] = useState<
    Readonly<{ label: string; value: string }[]>
  >([]);

  const [userInput, setUserInput] = useState<AssignedCourseRolesType>({});

  useEffect(() => {
    const newUserInput: typeof userInput = {};
    const existingCoursesPaths = Object.keys(userInput);
    selectedCourses.forEach((course) => {
      if (existingCoursesPaths.includes(course.value)) {
        // if existing in the userInput state, persist the state
        newUserInput[course.value] = userInput[course.value];
      } else {
        // if not existing in the userInput state, initiate with default value
        newUserInput[course.value] = {
          role: 'user',
          cohortRoles: {},
        };
      }
    });
    setUserInput({
      ...newUserInput,
    });
  }, [selectedCourses]);

  const removeSelectedCourses = (removedValue: string): void => {
    setSelectedCourses((prev) =>
      prev.filter(({ value }) => value !== removedValue)
    );
  };

  // Should only be called when user course role equals to staff
  const updateCohortOption = (
    coursePath: string,
    cohorts: ClassStateType
  ): void => {
    setUserInput((prev) => ({
      ...prev,
      [coursePath]: {
        role: 'user',
        cohortRoles: cohorts.reduce(
          (prev, currentCohort) => ({ ...prev, [currentCohort]: true }),
          {}
        ),
      },
    }));
  };

  const coursesLookupMap = useMemo(() => {
    const coursesMapByPath: { [path: string]: CourseEnrolment } = {};
    courses.forEach((course) => {
      coursesMapByPath[course.path] = course;
    });
    return coursesMapByPath;
  }, [courses]);

  const coursesOptions = useMemo(
    () =>
      courses.map((course) => ({
        label: course.name,
        value: course.path,
      })),
    [courses]
  );

  return (
    <ModalDialog
      dialogClassName="enrol-to-courses"
      show
      center
      small
      onHide={onHide}
      title="Enrol learners in courses"
      actions={[
        {
          label: 'Assign to courses',
          showSpinner: isAssigningFromCoursesLoading,
          disabled: isAssigningFromCoursesLoading,
          onClick: () => onAssignToCourses(userInput),
          type: 'primary-kelp',
        },
      ]}
    >
      <TotalSelectedMembers>
        {totalSelectedMembers} learner{totalSelectedMembers > 1 ? 's' : ''}
      </TotalSelectedMembers>
      <MultiSelect
        closeMenuOnSelect
        hideSelectedOptions
        label="Select courses"
        placeholder="Add a course"
        controlShouldRenderValue={false}
        options={coursesOptions}
        value={selectedCourses}
        onChange={(values) => setSelectedCourses(values)}
      />
      {selectedCourses.length > 0 && (
        <CoursesListTable>
          <CourseItemTr>
            <CourseItemTh>Course</CourseItemTh>
            <CourseItemTh>Class</CourseItemTh>
            <CourseItemTh />
          </CourseItemTr>
          {selectedCourses.map(({ label, value: coursePath }) => (
            <CourseItemTr>
              <CourseItemTd>
                <CourseDetailItem>
                  <img src={coursesLookupMap[coursePath].promoImage} alt="" />
                  <div>
                    <CourseName>{label}</CourseName>
                    <CoursePath>{coursePath}</CoursePath>
                  </div>
                </CourseDetailItem>
              </CourseItemTd>

              <CourseItemTd>
                <ClassSelectWrapper>
                  <ClassOptions
                    institutionPath={institutionPath}
                    coursePath={coursePath}
                    onChange={(newCohortRoles) =>
                      updateCohortOption(coursePath, newCohortRoles)
                    }
                  />
                </ClassSelectWrapper>
              </CourseItemTd>
              <CourseItemTd>
                <StyledTimesButton
                  type="button"
                  onClick={() => removeSelectedCourses(coursePath)}
                >
                  <FontAwesomeIcon icon={faTimes} />
                </StyledTimesButton>
              </CourseItemTd>
            </CourseItemTr>
          ))}
        </CoursesListTable>
      )}
      <ErrorMessage>{errorMessage}</ErrorMessage>
    </ModalDialog>
  );
};
